import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useDashboardTracking } from '../../hooks/useDashboardTracking';
import DateTimePickerInput from '../../components/DateTimePickerInput';

// Chart components
import PlannedChangesChart from './components/charts/PlannedChangesChart';
import ChangeStateBarChart from './components/charts/ChangeStateBarChart';
import ChangeTypeBarChart from './components/charts/ChangeTypeBarChart';
import GroupWiseChangesChart from './components/charts/GroupWiseChangesChart';
import CustomerWiseChangesChart from './components/charts/CustomerWiseChangesChart';
import FailedChangesTable from './components/FailedChangesTable';
import UnifiedPulseReportTable from './components/UnifiedPulseReportTable';

// Utils
import { DurationPreset, durationPresets, convertPresetToTimeRange, getDurationPresetRange } from './utils/dateHelpers';

interface Customer {
  id: string;
  accountName: string;
}

interface Group {
  id: string;
  name: string;
}

interface ChartFilter {
  changeState?: string;
  changeType?: string;
  filterType?: string;
  filterValue?: string;
}

const ChangeRequestDashboard: React.FC = () => {
  // Subscribe to real-time change updates via SSE
  useTicketEvents({ ticketTypes: ['CHANGE'] });

  // Get date formatter hook
  const { formatTime } = useDateFormatter();

  // Filter state
  const [customerId, setCustomerId] = useState<string>('');
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('this_month');
  const [previousPreset, setPreviousPreset] = useState<DurationPreset>('this_month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');

  // Chart interaction state
  const [tableVisible, setTableVisible] = useState(false);
  const [tableFilterLabel, setTableFilterLabel] = useState('');
  const [chartFilters, setChartFilters] = useState<ChartFilter>({});

  // Handle duration preset change
  const handleDurationChange = (newPreset: DurationPreset) => {
    if (newPreset === 'custom') {
      // Pre-populate custom dates based on the previous preset's range
      const range = getDurationPresetRange(previousPreset);
      setCustomDateFrom(range.from);
      setCustomDateTo(range.to);
      // Don't set applied dates yet - wait for Apply button
    } else {
      // For non-custom presets, update the previous preset tracker
      setPreviousPreset(newPreset);
      // Clear custom date state
      setCustomDateFrom('');
      setCustomDateTo('');
      setAppliedDateFrom('');
      setAppliedDateTo('');
    }
    setDurationPreset(newPreset);
  };

  // Compute time range for API - uses applied dates for custom range
  const timeRange = useMemo(() => {
    if (durationPreset === 'custom') {
      // Only return custom range if dates have been applied
      if (appliedDateFrom && appliedDateTo) {
        return convertPresetToTimeRange('custom', appliedDateFrom, appliedDateTo);
      }
      // Keep using the previous preset's range until Apply is clicked
      return convertPresetToTimeRange(previousPreset);
    }
    return convertPresetToTimeRange(durationPreset);
  }, [durationPreset, appliedDateFrom, appliedDateTo, previousPreset]);

  // Handle applying custom date range
  const handleApplyCustomDates = () => {
    if (customDateFrom && customDateTo) {
      setAppliedDateFrom(customDateFrom);
      setAppliedDateTo(customDateTo);
    }
  };

  // Check if custom dates have been modified and need to be applied
  const customDatesNeedApply = durationPreset === 'custom' &&
    customDateFrom && customDateTo &&
    (customDateFrom !== appliedDateFrom || customDateTo !== appliedDateTo);

  // Track dashboard filters for Application Insights
  useDashboardTracking('ChangeRequest', {
    customerId,
    durationPreset,
    assignmentGroupId,
    customDateFrom: appliedDateFrom || undefined,
    customDateTo: appliedDateTo || undefined,
  });

  // Get timezone offset
  const timezoneOffset = new Date().getTimezoneOffset();

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data.customers || response.data || [];
    },
  });

  // Fetch all assignment groups for filtering
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['all-assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment&limit=500');
      return response.data.groups || response.data || [];
    },
  });

  // Fetch dashboard data with fresh data on every mount/tab focus
  const { data: dashboardData, isLoading, error, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['change-dashboard', customerId, timeRange, assignmentGroupId, timezoneOffset],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (timeRange) params.append('timeRange', timeRange);
      if (assignmentGroupId) params.append('assignmentGroupId', assignmentGroupId);
      params.append('timezoneOffset', String(timezoneOffset));

      const response = await api.get(`/dashboard/change-request?${params}`);
      return response.data;
    },
    staleTime: 0,               // Always fetch fresh data on mount
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: 'always',   // Always refetch on component mount
  });

  // Chart click handlers
  const handlePlannedChartClick = (filterType: string) => {
    const labels: Record<string, string> = {
      plannedToday: 'Planned Changes Today',
      plannedThisWeek: 'Planned Changes This Week',
      emergency: 'Emergency Changes',
    };
    setTableFilterLabel(labels[filterType] || filterType);
    setChartFilters({ filterType, filterValue: filterType });
    setTableVisible(true);
  };

  const handleStateClick = (state: string) => {
    const stateLabels: Record<string, string> = {
      new: 'New',
      assess: 'Assess',
      authorize: 'Authorize',
      scheduled: 'Scheduled',
      implement: 'Implement',
      review: 'Review',
      closed: 'Closed',
      cancelled: 'Cancelled',
      on_hold: 'On Hold',
    };
    setTableFilterLabel(`${stateLabels[state] || state} Changes`);
    setChartFilters({ changeState: state });
    setTableVisible(true);
  };

  const handleTypeClick = (type: string) => {
    const typeLabels: Record<string, string> = {
      normal: 'Normal',
      standard: 'Standard',
      emergency: 'Emergency',
      infrastructure: 'Infrastructure',
    };
    setTableFilterLabel(`${typeLabels[type] || type} Changes`);
    setChartFilters({ changeType: type });
    setTableVisible(true);
  };

  const handleGroupClick = (groupId: string, groupName: string) => {
    setTableFilterLabel(`Changes by ${groupName}`);
    setChartFilters({ filterType: 'assignmentGroup', filterValue: groupId });
    setTableVisible(true);
  };

  const handleWeekendGroupClick = (groupId: string, groupName: string) => {
    setTableFilterLabel(`Weekend Changes by ${groupName}`);
    setChartFilters({ filterType: 'weekendActivity', filterValue: groupId });
    setTableVisible(true);
  };

  const handleCustomerClick = (clickedCustomerId: string, customerName: string) => {
    setTableFilterLabel(`Changes for ${customerName}`);
    setChartFilters({ filterType: 'customer', filterValue: clickedCustomerId });
    setTableVisible(true);
  };

  const handleViewAllFailed = () => {
    setTableFilterLabel('Failed Changes');
    setChartFilters({ filterType: 'failed', filterValue: 'all' });
    setTableVisible(true);
  };

  // Tile click handlers
  const handleTotalChangesClick = () => {
    setTableFilterLabel('Total Changes');
    setChartFilters({ changeState: 'all' });
    setTableVisible(true);
  };

  const handleOpenChangesClick = () => {
    setTableFilterLabel('Open Changes');
    setChartFilters({ filterType: 'open', filterValue: 'all' });
    setTableVisible(true);
  };

  const handleClosedChangesClick = () => {
    setTableFilterLabel('Closed Changes');
    setChartFilters({ changeState: 'closed' });
    setTableVisible(true);
  };

  const handleSuccessfulChangesClick = () => {
    setTableFilterLabel('Successful Changes');
    setChartFilters({ filterType: 'successful', filterValue: 'all' });
    setTableVisible(true);
  };

  const handleFailedChangesClick = () => {
    setTableFilterLabel('Failed Changes');
    setChartFilters({ filterType: 'failed', filterValue: 'all' });
    setTableVisible(true);
  };

  const closeTable = () => {
    setTableVisible(false);
    setChartFilters({});
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!dashboardData) return;
    // PDF export implementation - to be added
    console.log('Export PDF functionality to be implemented');
  };

  const selectedCustomerName = customers.find(c => c.id === customerId)?.accountName || 'All Customers';

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Customers</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.accountName}
                </option>
              ))}
            </select>
          </div>

          {/* Duration Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={durationPreset}
              onChange={(e) => handleDurationChange(e.target.value as DurationPreset)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              {durationPresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group</label>
            <select
              value={assignmentGroupId}
              onChange={(e) => setAssignmentGroupId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export PDF Button and Refresh */}
          <div className="flex items-end gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Refresh dashboard data"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!dashboardData}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Last Updated Indicator */}
        {dataUpdatedAt && (
          <div className="mt-3 flex items-center justify-end text-xs text-gray-500">
            {isFetching && (
              <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
            )}
            <span>
              Last updated: {formatTime(new Date(dataUpdatedAt))}
            </span>
          </div>
        )}

        {/* Custom Date Range (shown when custom is selected) */}
        {durationPreset === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <DateTimePickerInput
                value={customDateFrom}
                onChange={(val) => setCustomDateFrom(val)}
                showTimeSelect={true}
                className="w-full"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <DateTimePickerInput
                  value={customDateTo}
                  onChange={(val) => setCustomDateTo(val)}
                  showTimeSelect={true}
                  className="w-full"
                />
              </div>
              <button
                onClick={handleApplyCustomDates}
                disabled={!customDateFrom || !customDateTo}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  customDatesNeedApply
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500">Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load dashboard data. Please try again.
          <button onClick={() => refetch()} className="ml-4 text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      {/* Dashboard Content */}
      {dashboardData && !isLoading && (
        <>
          {/* Summary Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all"
              onClick={handleTotalChangesClick}
            >
              <div className="text-2xl font-bold text-gray-900">{dashboardData.summary?.total || 0}</div>
              <div className="text-sm text-gray-500">Total Changes</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all"
              onClick={handleOpenChangesClick}
            >
              <div className="text-2xl font-bold text-blue-600">{dashboardData.summary?.open || 0}</div>
              <div className="text-sm text-gray-500">Open</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-green-50 transition-all"
              onClick={handleClosedChangesClick}
            >
              <div className="text-2xl font-bold text-green-600">{dashboardData.summary?.closed || 0}</div>
              <div className="text-sm text-gray-500">Closed</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-emerald-50 transition-all"
              onClick={handleSuccessfulChangesClick}
            >
              <div className="text-2xl font-bold text-emerald-600">{dashboardData.summary?.successful || 0}</div>
              <div className="text-sm text-gray-500">Successful</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md hover:bg-red-50 transition-all"
              onClick={handleFailedChangesClick}
            >
              <div className="text-2xl font-bold text-red-600">{dashboardData.summary?.failed || 0}</div>
              <div className="text-sm text-gray-500">Failed</div>
            </div>
          </div>

          {/* Row 1: Planned Changes and Type Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PlannedChangesChart
              plannedToday={dashboardData.plannedChanges?.today || 0}
              plannedThisWeek={dashboardData.plannedChanges?.thisWeek || 0}
              emergencyChanges={dashboardData.plannedChanges?.emergency || 0}
              onBarClick={handlePlannedChartClick}
            />
            <ChangeTypeBarChart
              data={dashboardData.byType || { normal: 0, standard: 0, emergency: 0 }}
              onBarClick={handleTypeClick}
            />
          </div>

          {/* Row 2: State Distribution and Weekend Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChangeStateBarChart
              data={dashboardData.byState || {
                new: 0, assess: 0, authorize: 0, scheduled: 0,
                implement: 0, review: 0, closed: 0, cancelled: 0, on_hold: 0
              }}
              onBarClick={handleStateClick}
            />
            <GroupWiseChangesChart
              data={dashboardData.weekendActivity || []}
              title="Weekend Activity by Assignment Group"
              chartId="chart-weekend-activity"
              barColor="rgba(249, 115, 22, 0.8)"
              onBarClick={handleWeekendGroupClick}
            />
          </div>

          {/* Row 3: Assignment Group and Customer Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GroupWiseChangesChart
              data={dashboardData.byAssignmentGroup || []}
              title="Assignment Group-wise Changes"
              chartId="chart-group-wise-changes"
              onBarClick={handleGroupClick}
            />
            <CustomerWiseChangesChart
              data={dashboardData.byCustomer || []}
              onBarClick={handleCustomerClick}
              title="Customer-wise Change Requests"
            />
          </div>

          {/* Row 4: Failed Changes Table */}
          <FailedChangesTable
            data={dashboardData.failedChanges || []}
            onViewAll={handleViewAllFailed}
          />

          {/* Filtered Tickets Table */}
          <UnifiedPulseReportTable
            visible={tableVisible}
            filterLabel={tableFilterLabel}
            baseFilters={{
              customerId,
              timeRange,
              assignmentGroupId,
              timezoneOffset,
            }}
            chartFilters={{
              ticketType: 'CHANGE',
              changeState: chartFilters.changeState,
              changeType: chartFilters.changeType,
              filterType: chartFilters.filterType,
              filterValue: chartFilters.filterValue,
            }}
            onClose={closeTable}
            ticketType="CHANGE"
            customerName={selectedCustomerName}
          />
        </>
      )}
    </div>
  );
};

export default ChangeRequestDashboard;
